import { useState, useEffect } from 'react';
import { ADMIN_ENDPOINTS, adminFetch } from '../config';
import './Admin.css';

const SETTING_KEYS = ['contact_subjects', 'site_name', 'copyright_text', 'chatbot_prompt', 'chat_notify_email', 'chat_notify_phone', 'site_base_url'];

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    adminFetch(ADMIN_ENDPOINTS.fetchSettings)
      .then((r) => r.json())
      .then((data) => {
        if (!data.error) setSettings(data);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setSaving(true);
    try {
      const res = await adminFetch(ADMIN_ENDPOINTS.saveSettings, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings }),
      });
      const data = await res.json();
      if (data.error) setMessage(data.error);
      else setMessage('Saved.');
    } catch {
      setMessage('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  if (loading) return <p>Loading…</p>;

  return (
    <>
      <h1>Site Settings</h1>
      <form onSubmit={handleSubmit}>
        {message && <p style={{ color: message.includes('Saved') ? '#3fb950' : 'var(--admin-danger)' }}>{message}</p>}
        <div className="form-group">
          <label>Contact subjects (comma-separated)</label>
          <textarea rows={3} value={settings.contact_subjects || ''} onChange={(e) => update('contact_subjects', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Site name</label>
          <input type="text" value={settings.site_name || ''} onChange={(e) => update('site_name', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Copyright text</label>
          <input type="text" value={settings.copyright_text || ''} onChange={(e) => update('copyright_text', e.target.value)} placeholder={`Copyright 2020 - ${new Date().getFullYear()} © - All Rights Reserved`} />
        </div>
        <div className="form-group">
          <label>Chatbot system prompt</label>
          <textarea rows={6} value={settings.chatbot_prompt || ''} onChange={(e) => update('chatbot_prompt', e.target.value)} placeholder="Instructions for the chatbot. Leave empty to use default." />
          <small style={{ color: '#888' }}>This prompt defines how the chatbot behaves. Site context is appended automatically when relevant.</small>
        </div>
        <h2>Chat handoff notifications</h2>
        <p className="page-subtitle">When a visitor clicks &quot;Talk to a human&quot;, you can be notified by email and optionally by text (SMS). The message includes a link to join the chat.</p>
        <div className="form-group">
          <label>Notification email</label>
          <input type="email" value={settings.chat_notify_email || ''} onChange={(e) => update('chat_notify_email', e.target.value)} placeholder="you@example.com" />
          <small style={{ color: '#888' }}>Receives an email with a link to join the chat. Leave empty to use the first admin user&apos;s email.</small>
        </div>
        <div className="form-group">
          <label>Notification phone (SMS)</label>
          <input type="text" value={settings.chat_notify_phone || ''} onChange={(e) => update('chat_notify_phone', e.target.value)} placeholder="+1234567890" />
          <small style={{ color: '#888' }}>Optional. For SMS you must configure Twilio (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER) in your server environment or config.</small>
        </div>
        <div className="form-group">
          <label>Site base URL (for links in emails/texts)</label>
          <input type="url" value={settings.site_base_url || ''} onChange={(e) => update('site_base_url', e.target.value)} placeholder="https://yoursite.com" />
          <small style={{ color: '#888' }}>Optional. Used to build the &quot;Join chat&quot; link. If empty, the link is built from the current request host.</small>
        </div>
        <button type="submit" className="btn" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </form>
    </>
  );
}
